import { useEffect, useState } from "react";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Checkbox, TextInput } from "@/components/ui/Field";
import type { AuftragLookup } from "@/lib/material";
import { toast } from "@/lib/toast";
import { AngebotPositionSelect } from "@/pages/admin/zeiterfassung/AngebotPositionSelect";
import { getCachedEmployeeTimeProfile } from "@/pages/admin/zeiterfassung/captureCalendarCache";
import { DayEntryTable } from "@/pages/admin/zeiterfassung/DayEntryTable";
import {
  EmployeeLockedAutocomplete,
  employeeLoginToOption,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import { ProjectAuftragAutocomplete } from "@/pages/admin/zeiterfassung/ProjectAuftragAutocomplete";
import { TimerWidget } from "@/pages/admin/zeiterfassung/TimerWidget";

type CaptureMode = "day" | "timer";

interface SelectedEmployee {
  login: string;
  display: string;
}

interface EntryCapturePanelProps {
  defaultEmployee: SelectedEmployee;
  onCreated: () => void;
  allowEmployeePicker?: boolean;
}

export function EntryCapturePanel({
  defaultEmployee,
  onCreated,
  allowEmployeePicker = true,
}: EntryCapturePanelProps) {
  const [mode, setMode] = useState<CaptureMode>("day");
  const [employeeOption, setEmployeeOption] = useState<AutocompleteOption<EmployeeOption> | null>(
    () => employeeLoginToOption(defaultEmployee.login, defaultEmployee.display),
  );
  const [project, setProject] = useState<AutocompleteOption<AuftragLookup> | null>(null);
  const [positionId, setPositionId] = useState<number | null>(null);
  const [positionRequired, setPositionRequired] = useState(false);
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [isHomeoffice, setIsHomeoffice] = useState(false);
  const [homeofficeAllowed, setHomeofficeAllowed] = useState(false);

  useEffect(() => {
    if (defaultEmployee.login.trim()) {
      setEmployeeOption(employeeLoginToOption(defaultEmployee.login, defaultEmployee.display));
    }
  }, [defaultEmployee.login, defaultEmployee.display]);

  const employee = employeeOption?.value ?? { login: "", display: "" };

  useEffect(() => {
    const login = employee.login.trim();
    if (!login || mode !== "timer") {
      setHomeofficeAllowed(false);
      setIsHomeoffice(false);
      return;
    }
    let cancelled = false;
    void getCachedEmployeeTimeProfile(login)
      .then((profile) => {
        if (cancelled) return;
        const allowed =
          profile.homeoffice_allowed ?? (!profile.is_monteur && !profile.project_required);
        setHomeofficeAllowed(allowed);
        if (!allowed) setIsHomeoffice(false);
      })
      .catch(() => {
        if (!cancelled) {
          setHomeofficeAllowed(false);
          setIsHomeoffice(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [employee.login, mode]);

  return (
    <div className="space-y-4">
      <div className="mx-auto max-w-3xl">
        <SegmentedControl
          value={mode}
          onChange={setMode}
          name="Erfassungsart"
          size="md"
          className="flex w-full md:inline-flex md:w-auto"
          options={[
            { value: "day", label: "Tageserfassung" },
            { value: "timer", label: "Live-Timer" },
          ]}
        />
      </div>

      {mode === "day" && (
        <DayEntryTable
          defaultEmployee={defaultEmployee}
          onSaved={onCreated}
          allowEmployeePicker={allowEmployeePicker}
        />
      )}

      {mode === "timer" && (
        <div className="mx-auto max-w-2xl space-y-5 rounded-ios border border-separator bg-card p-5 shadow-ios md:p-6">
          <ProjectAuftragAutocomplete
            label="Projekt"
            value={project}
            onChange={(next) => {
              setProject(next);
              setPositionId(null);
              setPositionRequired(false);
            }}
            forUserLogin={employee.login || null}
          />

          <AngebotPositionSelect
            auftragId={project?.value.id ?? null}
            value={positionId}
            onChange={setPositionId}
            onRequiredChange={setPositionRequired}
          />

          {allowEmployeePicker ? (
            <EmployeeLockedAutocomplete
              value={employeeOption}
              onChange={setEmployeeOption}
            />
          ) : (
            <label className="space-y-1">
              <span className="eh-label">Mitarbeiter</span>
              <div className="eh-input flex items-center bg-fill-tertiary/40 text-label">
                {employee.display || employee.login}
              </div>
            </label>
          )}

          {!showComment ? (
            <button
              type="button"
              className="text-sm text-label-secondary hover:text-accent"
              onClick={() => setShowComment(true)}
            >
              + Kommentar hinzufügen
            </button>
          ) : (
            <TextInput
              label="Kommentar"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional"
            />
          )}

          {homeofficeAllowed && (
            <Checkbox
              id="timer-homeoffice"
              label="Homeoffice"
              description="Wird beim Stoppen auf den Zeiteintrag übernommen"
              checked={isHomeoffice}
              onChange={(e) => setIsHomeoffice(e.target.checked)}
            />
          )}

          <TimerWidget
            userLogin={employee.login}
            auftragId={project?.value.id ?? null}
            positionId={positionId}
            positionRequired={positionRequired}
            comment={comment}
            isHomeoffice={homeofficeAllowed && isHomeoffice}
            embedded
            onStopped={() => {
              toast.success("Timer gestoppt und gebucht.");
              onCreated();
            }}
          />
        </div>
      )}
    </div>
  );
}
